// Pure, DevExtreme-independent grouping/summary engine — the piece that has never existed
// outside DevExtreme before. Buckets a flat row array by an arbitrary number of group-by
// fields (0, 1, or N — same code path regardless of depth), then computes group-level and
// grand-total summaries generically from a `summary.groupItems`/`summary.totalItems`-shaped
// config, the same shape already read off a live grid via `grid.option('summary.groupItems')`.
import { runCustomSummary } from './customSummaryDriver';

// GroupNode = { level, field, key, rows, children: GroupNode[] | null, groupSummary?: object }
function bucketByField(rows, groupByFields, level) {
    const field = groupByFields[level];
    const buckets = new Map();
    const order = [];
    rows.forEach((row) => {
        const key = row[field];
        if (!buckets.has(key)) {
            buckets.set(key, []);
            order.push(key);
        }
        buckets.get(key).push(row);
    });

    const isLeafLevel = level === groupByFields.length - 1;
    return order.map((key) => {
        const bucketRows = buckets.get(key);
        return {
            level,
            field,
            key,
            rows: bucketRows,
            children: isLeafLevel ? null : bucketByField(bucketRows, groupByFields, level + 1),
        };
    });
}

export function buildGroupTree(rows, groupByFields) {
    if (!groupByFields || !groupByFields.length) {
        return { level: -1, field: null, key: null, rows, children: null };
    }
    return { level: -1, field: null, key: null, rows, children: bucketByField(rows, groupByFields, 0) };
}

const REDUCERS = {
    sum: (values) => values.reduce((acc, v) => acc + (typeof v === 'number' ? v : 0), 0),
    count: (values) => values.length,
    min: (values) => values.reduce((acc, v) => (typeof v === 'number' && (acc === undefined || v < acc) ? v : acc), undefined),
    max: (values) => values.reduce((acc, v) => (typeof v === 'number' && (acc === undefined || v > acc) ? v : acc), undefined),
    avg: (values) => {
        const nums = values.filter((v) => typeof v === 'number');
        return nums.length ? nums.reduce((acc, v) => acc + v, 0) / nums.length : 0;
    },
};

// Computes one summary value per configured item (mirrors DevExtreme's
// summary.groupItems/totalItems array shape: { column, summaryType, name? }). Deliberately
// keyed by `column` (or `name` as a fallback), not by array index like DevExtreme's own
// internal groupSummaryItems — this renderer places exactly one summary value per column's
// cell in a summary row, so "one summaryType per column" is the model that matters here. A
// config with two DIFFERENT summaryTypes on the SAME column would have the later one win;
// that's an accepted limitation, not a bug, since there's nowhere to render a second value in
// that same cell anyway.
export function computeSummaryItems(rows, items, ctx) {
    if (!items || !items.length) return {};
    const result = {};
    items.forEach((item, index) => {
        const key = item.column || item.name || `summary_${index}`;
        if (item.summaryType === 'custom') {
            result[key] = runCustomSummary(ctx.calculateCustomSummary, item.name || item.column, rows, ctx.component);
        } else {
            const values = rows.map((row) => row[item.column]);
            const reducer = REDUCERS[item.summaryType] || REDUCERS.sum;
            result[key] = reducer(values);
        }
    });
    return result;
}

// Annotates every group-level node in the tree with its own groupSummary, recursively —
// same computeSummaryItems call regardless of nesting depth.
export function annotateSummaries(node, groupItems, ctx) {
    if (node.level >= 0) {
        node.groupSummary = computeSummaryItems(node.rows, groupItems, ctx);
    }
    if (node.children) {
        node.children.forEach((child) => annotateSummaries(child, groupItems, ctx));
    }
}

export function computeGrandTotal(tree, totalItems, ctx) {
    return computeSummaryItems(tree.rows, totalItems, ctx);
}
